import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "gl_cart_v1";

export const CartProvider = ({ children }) => {
  // Hydrate synchronously (lazy initialiser) so the persist effect below can
  // never overwrite the stored cart with [] before it has been read
  // (React StrictMode double-invokes mount effects in dev).
  const readStored = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {}; } catch (_e) { return {}; }
  };
  const [items, setItems] = useState(() => readStored().items || []);          // {productId, slug, name, image, price, variantName, quantity, bundleKey?}
  const [bundleCodes, setBundleCodes] = useState(() => readStored().bundleCodes || []); // ["look-of-{slug}"]
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, bundleCodes }));
  }, [items, bundleCodes]);

  const addItem = useCallback((item) => {
    // Cap merge to available stock so a cart merge can never overshoot the
    // variant's real inventory. `stock` is passed from the PDP/collection
    // card at add-time; when omitted we assume unlimited (bundle items).
    const stockCap = typeof item.stock === "number" ? Math.max(0, item.stock) : Infinity;
    setItems((prev) => {
      const key = `${item.productId}__${item.variantName || ""}__${item.bundleKey || ""}`;
      const existing = prev.find((p) => `${p.productId}__${p.variantName || ""}__${p.bundleKey || ""}` === key);
      const desired = item.quantity || 1;
      if (existing) {
        const nextQty = Math.min(existing.quantity + desired, stockCap);
        return prev.map((p) =>
          p === existing ? { ...p, quantity: nextQty, stock: stockCap } : p,
        );
      }
      const initialQty = Math.min(desired, stockCap);
      // If stock is 0 we bail — the ATC button should have prevented this
      // already; this is a defence-in-depth guard.
      if (initialQty <= 0) return prev;
      return [...prev, { ...item, quantity: initialQty, stock: stockCap }];
    });
    setDrawerOpen(true);
  }, []);

  const addBundle = useCallback((bundleKey, items) => {
    setItems((prev) => {
      // remove any prior items in this bundle (idempotent)
      const filtered = prev.filter((p) => p.bundleKey !== bundleKey);
      return [...filtered, ...items.map((i) => ({ ...i, bundleKey, quantity: 1 }))];
    });
    setBundleCodes((prev) => Array.from(new Set([...prev, bundleKey])));
    setDrawerOpen(true);
  }, []);

  // Keep stored cart prices in line with live (campaign) prices from the catalog.
  const syncPrices = useCallback((products) => {
    const byId = new Map(products.map((p) => [p.id, p]));
    setItems((prev) => {
      let changed = false;
      const next = prev.map((it) => {
        const p = byId.get(it.productId);
        if (!p || (p.price === it.price && (p.original_price || undefined) === (it.originalPrice || undefined))) return it;
        changed = true;
        return { ...it, price: p.price, originalPrice: p.original_price };
      });
      return changed ? next : prev;
    });
  }, []);

  const removeItem = useCallback((index) => {
    setItems((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      // if removing the last item of a bundle, clear that bundle code
      if (removed?.bundleKey) {
        const stillHas = next.some((it) => it.bundleKey === removed.bundleKey);
        if (!stillHas) setBundleCodes((b) => b.filter((c) => c !== removed.bundleKey));
      }
      return next;
    });
  }, []);

  const updateQuantity = useCallback((index, qty) => {
    setItems((prev) => {
      const it = prev[index];
      if (!it) return prev;
      // Zero or negative → drop the row entirely (this is what shoppers
      // expect when they tap "−" on a qty-1 line).
      if (qty <= 0) {
        const removed = it;
        const next = prev.filter((_, i) => i !== index);
        if (removed?.bundleKey) {
          const stillHas = next.some((x) => x.bundleKey === removed.bundleKey);
          if (!stillHas) setBundleCodes((b) => b.filter((c) => c !== removed.bundleKey));
        }
        return next;
      }
      // Cap upper bound at the snapshotted stock; if `stock` wasn't recorded
      // (older cart entries), leave the qty alone.
      const cap = typeof it.stock === "number" ? it.stock : Infinity;
      const clamped = Math.min(qty, cap);
      return prev.map((p, i) => (i === index ? { ...p, quantity: clamped } : p));
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setBundleCodes([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const subtotal = useMemo(() => items.reduce((s, it) => s + it.price * it.quantity, 0), [items]);
  const hasBundle = bundleCodes.length > 0;
  const bundleDiscount = hasBundle ? subtotal * 0.10 : 0;
  const total = subtotal - bundleDiscount;
  const count = items.reduce((s, it) => s + it.quantity, 0);

  const value = useMemo(
    () => ({
      items, bundleCodes, count, subtotal, bundleDiscount, total, hasBundle,
      addItem, addBundle, syncPrices, removeItem, updateQuantity, clear,
      drawerOpen, setDrawerOpen,
    }),
    [items, bundleCodes, count, subtotal, bundleDiscount, total, hasBundle, addItem, addBundle, syncPrices, removeItem, updateQuantity, clear, drawerOpen],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => useContext(CartCtx);
